import uid, { containsUid } from "common/utils/uid";
import type { DocMigration } from "../docMigration";

const OPTION_SET_ID_PREFIX = "optionSet_";
const OPTION_ID_PREFIX = "option_";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_435: DocMigration = {
  maxSchemaVersion: "0.0.435",
  description: "Restructure entityRefs entity & table format",
  apply(json: any) {
    if (!json.entityRefs) {
      return json;
    }

    for (const id in json.entityRefs) {
      const entityRef = json.entityRefs[id];

      if (id !== entityRef.entityId) {
        delete json.entityRefs[id];
        continue;
      }

      if (!containsUid(entityRef.entityId)) {
        delete json.entityRefs[id];
        continue;
      }

      const handle = docGetEntityHandleById(json, entityRef.entityId);

      if (!handle) {
        delete json.entityRefs[id];
        continue;
      }

      json.entityRefs[id] = {
        id,
        entityType: handle.type,
        docRefId: entityRef.docRefId,
        disabled: undefined,
        sortIndex: undefined,
      };
    }

    return json;
  },
};

export default migration_0_0_435;

const settingsTypeIsCatalogSettingsTypeTable = {
  // catalogs
  ANNOTATION_CATALOG_SETTINGS: true,
  CASEWORK_CATALOG_SETTINGS: true,
  DOOR_CATALOG_SETTINGS: true,
  WALL_CATALOG_SETTINGS: true,
  DATUM_CATALOG_SETTINGS: true,
  FLOOR_CATALOG_SETTINGS: true,
  STAIR_CATALOG_SETTINGS: true,
  COLUMN_CATALOG_SETTINGS: true,
  WINDOW_CATALOG_SETTINGS: true,
  DRAWING_CATALOG_SETTINGS: true,
  PROFILE_CATALOG_SETTINGS: true,
  RAILING_CATALOG_SETTINGS: true,
  FOUNDATION_CATALOG_SETTINGS: true,
  ELECTRICAL_ITEM_CATALOG_SETTINGS: true,
  EXTERIOR_PROP_SETTINGS: true,
  INTERIOR_PROP_SETTINGS: true,

  // not catalogs
  WAINSCOTING_SETTINGS: false,
  WALL_FRAMING_SETTINGS: false,
  PORCH_SETTINGS: false,
  REAR_PORCH_SETTINGS: false,
  ROOF_SETTINGS: false,
  DORMER_SETTINGS: false,
  ROOF_FRAMING_SETTINGS: false,
  WINDOW_FRAMING_SETTINGS: false,
  DOOR_FRAMING_SETTINGS: false,
  WINDOW_WRAPPING_SETTINGS: false,
  DOOR_WRAPPING_SETTINGS: false,
  GARAGE_SETTINGS: false,
  DOOR_SETTINGS: false,
  WINDOW_SETTINGS: false,
  WINDOW_MATERIAL_SETTINGS: false,
  AWNING_SETTINGS: false,
  EXTERIOR_TRIM_SETTINGS: false,
  PLAN_SETTINGS: false,
  MASTER_PLAN_SETTINGS: false,
  SCHEDULE_SETTINGS: false,
  KEY_MEASURES_SETTINGS: false,
  FOUNDATION_SETTINGS: false,
  WALL_SETTINGS: false,
  INTERIOR_TRIM_SETTINGS: false,
  INTERIOR_MATERIAL_SETTINGS: false,
  STRUCTURE_SETTINGS: false,
  ELECTRICAL_SETTINGS: false,
  EXTERIOR_MATERIAL_SETTINGS: false,
  WINDOW_MULLION_SETTINGS: false,
  WINDOW_SHUTTER_SETTINGS: false,
  EXTERIOR_WINDOW_CASING_SETTINGS: false,
  EXTERIOR_DOOR_CASING_SETTINGS: false,
  INTERIOR_DOOR_CASING_SETTINGS: false,
  VIEW_NOTE_SETTINGS: false,
  CASEWORK_SETTINGS: false,
  FLOOR_SETTINGS: false,
  INTERIOR_WINDOW_CASING_SETTINGS: false,
};

function isCatalogSettings(setting: any): boolean {
  return setting?.type && settingsTypeIsCatalogSettingsTypeTable[setting.type];
}

const docEntityHandleTypes = {
  GROUP: "GROUP",
  ANNOTATION: "ANNOTATION",
  ANNOTATION_TEMPLATE: "ANNOTATION_TEMPLATE",
  ANNOTATION_TEMPLATE_COMPONENT: "ANNOTATION_TEMPLATE_COMPONENT",
  BLOCK: "BLOCK",
  CASEWORK: "CASEWORK",
  DOC_REF: "DOC_REF",
  ENTITY_REF: "ENTITY_REF",
  DATA_TABLE: "DATA_TABLE",
  DATA_SEQUENCE: "DATA_SEQUENCE",
  PRESET: "PRESET",
  BUILDER_REF: "BUILDER_REF",
  CATALOG_ITEM: "CATALOG_ITEM",
  OPTION_SET_REF: "OPTION_SET_REF",
  CEILING: "CEILING",
  DORMER: "DORMER",
  DRAWING_REF: "DRAWING_REF",
  EDIT_MODE: "EDIT_MODE",
  EDGE: "EDGE",
  EDGE_BEHAVIOR: "EDGE_BEHAVIOR",
  EDGE_MODIFIER: "EDGE_MODIFIER",
  ELECTRICAL_DEVICE: "ELECTRICAL_DEVICE",
  ELECTRICAL_LAYOUT: "ELECTRICAL_LAYOUT",
  ELECTRICAL_RULE: "ELECTRICAL_RULE",
  ELEVATION: "ELEVATION",
  FACADE: "FACADE",
  FACADE_SPAN_STRATUM: "FACADE_SPAN_STRATUM",
  FACADE_SWEEP_STRATUM: "FACADE_SWEEP_STRATUM",
  FOUNDATION: "FOUNDATION",
  FOUNDATION_SYSTEM: "FOUNDATION_SYSTEM",
  FLOOR: "FLOOR",
  FLOOR_FINISH: "FLOOR_FINISH",
  GABLE_OPENING: "GABLE_OPENING",
  LEVEL: "LEVEL",
  LEVEL_DATUM: "LEVEL_DATUM",
  MODIFIER: "MODIFIER",
  MEASUREMENT: "MEASUREMENT",
  OPENING: "OPENING",
  PALETTE: "PALETTE",
  OPTION_SET_OVERRIDES: "OPTION_SET_OVERRIDES",
  OPTION_SET: "OPTION_SET",
  OPTION: "OPTION",
  OPTION_RULE: "OPTION_RULE",
  OPTION_EFFECT: "OPTION_EFFECT",
  OPTION_LOCK: "OPTION_LOCK",
  AUX_OPTION_SET: "AUX_OPTION_SET",
  PROP_INSTANCE: "PROP_INSTANCE",
  PROP_DEF: "PROP_DEF",
  PROP_ATTRIBUTES: "PROP_ATTRIBUTES",
  RAILING: "RAILING",
  RAILING_POST: "RAILING_POST",
  REFERENCE_GEOM: "REFERENCE_GEOM",
  ROOF: "ROOF",
  ROOF_SIDE: "ROOF_SIDE",
  ROOF_SYSTEM: "ROOF_SYSTEM",
  ROOM: "ROOM",
  ROOM_LAYOUT: "ROOM_LAYOUT",
  ROOM_LAYOUT_INSTANCE: "ROOM_LAYOUT_INSTANCE",
  SETTINGS: "SETTINGS",
  SETTINGS_SET: "SETTINGS_SET",
  ENTITY_SETTINGS: "ENTITY_SETTINGS",
  SITE: "SITE",
  SITE_SHAPE: "SITE_SHAPE",
  OPTION_TEMPLATE: "OPTION_TEMPLATE",
  SHAPE: "SHAPE",
  SHEET: "SHEET",
  SKYLIGHT: "SKYLIGHT",
  STAIR_BLOCK: "STAIR_BLOCK",
  STAIR_SYSTEM: "STAIR_SYSTEM",
  UNDERLAY: "UNDERLAY",
  ASSET: "ASSET",
  VERTEX: "VERTEX",
  VIEW_3D: "VIEW_3D",
  PLAN_VIEW: "PLAN_VIEW",
  ELEVATION_VIEW: "ELEVATION_VIEW",
  SECTION_VIEW: "SECTION_VIEW",
  COLUMN: "COLUMN",
  GENERATED_ENTITY: "GENERATED_ENTITY",
  AUTO_DIMENSION: "AUTO_DIMENSION",
  PLAN_SET_TEMPLATE: "PLAN_SET_TEMPLATE",
  CONTENT_TEMPLATE: "CONTENT_TEMPLATE",
  CONTENT_TEMPLATE_INSTANCE: "CONTENT_TEMPLATE_INSTANCE",
  CONTENT_REPLICATOR: "CONTENT_REPLICATOR",
  VIEWPORT: "VIEWPORT",
  VIEW_TEMPLATE: "VIEW_TEMPLATE",
  LOCAL_PARAMETER: "LOCAL_PARAMETER",
  GLOBAL_PARAMETER: "GLOBAL_PARAMETER",
  SYSTEM_PARAMETER: "SYSTEM_PARAMETER",
  PLAN_SET_TEMPLATE_CONFIG: "PLAN_SET_TEMPLATE_CONFIG",
  ENTITY_TAG: "ENTITY_TAG",
  VIEW_TEMPLATE_OVERRIDE: "VIEW_TEMPLATE_OVERRIDE",
  ROOF_VOLUME: "ROOF_VOLUME",
  EFFECT: "EFFECT",
  VIEW_OVERRIDE: "VIEW_OVERRIDE",
  SELECTOR: "SELECTOR",
  VIEW_SELECTOR: "VIEW_SELECTOR",
  DATUM: "DATUM",
  RULE_BASED_SELECTOR: "RULE_BASED_SELECTOR",
  RULE_BASED_SELECTOR_INSTANCE: "RULE_BASED_SELECTOR_INSTANCE",
};

const docEntityTypeToDocTableName = {
  GROUP: "groups",
  DOC_REF: "docRefs",
  BUILDER_REF: "builderRefs",
  LEVEL: "levels",
  BLOCK: "blocks",
  ROOM: "rooms",
  STAIR_SYSTEM: "stairSystems",
  STAIR_BLOCK: "stairBlocks",
  RAILING: "railings",
  PRESET: "presets",
  ROOF_SYSTEM: "roofSystems",
  ROOF: "roofs",
  ROOF_VOLUME: "roofVolumes",
  FOUNDATION_SYSTEM: "foundationSystems",
  FOUNDATION: "foundations",
  UNDERLAY: "underlays",
  ASSET: "assets",
  REFERENCE_GEOM: "referenceGeom",
  ELEVATION: "elevations",
  PALETTE: "palettes",
  SETTINGS: "settings",
  SETTINGS_SET: "settingsSets",
  ENTITY_SETTINGS: "entitySettings",
  OPTION_TEMPLATE: "optionTemplates",
  SHEET: "sheets",
  DRAWING_REF: "drawingRefs",
  OPTION_SET_OVERRIDES: "optionSetOverrides",
  OPTION_SET: "optionSets",
  OPTION_RULE: "optionRules",
  OPTION_EFFECT: "optionEffects",
  OPTION_LOCK: "optionLocks",
  AUX_OPTION_SET: "auxOptionSets",
  PROP_INSTANCE: "propInstances",
  PROP_ATTRIBUTES: "propAttributes",
  PROP_DEF: "propDefs",
  CASEWORK: "casework",
  CEILING: "ceilings",
  EDGE_BEHAVIOR: "edgeBehaviors",
  OPENING: "openings",
  ANNOTATION: "annotation",
  FACADE: "facades",
  DORMER: "dormers",
  SITE: "sites",
  SITE_SHAPE: "siteShapes",
  MODIFIER: "modifiers",
  MEASUREMENT: "measurements",
  VIEW_3D: "view3Ds",
  ELEVATION_VIEW: "elevationViews",
  PLAN_VIEW: "planViews",
  OPTION_SET_REF: "optionSetRefs",
  ROOM_LAYOUT: "roomLayouts",
  ROOM_LAYOUT_INSTANCE: "roomLayoutInstances",
  SKYLIGHT: "skylights",
  GABLE_OPENING: "gableOpenings",
  COLUMN: "columns",
  FLOOR: "floors",
  FLOOR_FINISH: "floorFinishes",
  FACADE_SPAN_STRATUM: "facadeSpanStrata",
  FACADE_SWEEP_STRATUM: "facadeSweepStrata",
  RAILING_POST: "railingPosts",
  EDGE_MODIFIER: "edgeModifiers",
  ELECTRICAL_DEVICE: "electricalDevices",
  ELECTRICAL_LAYOUT: "electricalLayouts",
  PLAN_SET_TEMPLATE: "planSetTemplates",
  CONTENT_TEMPLATE: "contentTemplates",
  CONTENT_TEMPLATE_INSTANCE: "contentTemplateInstances",
  CONTENT_REPLICATOR: "contentReplicators",
  LOCAL_PARAMETER: "localParameters",
  GLOBAL_PARAMETER: "globalParameters",
  SYSTEM_PARAMETER: undefined,
  VIEWPORT: "viewports",
  VIEW_TEMPLATE: "viewTemplates",
  VIEW_TEMPLATE_OVERRIDE: "viewTemplateOverrides",
  DATA_TABLE: "dataTables",
  DATA_SEQUENCE: "dataSequences",
  SECTION_VIEW: "sectionViews",
  SELECTOR: "selectors",
  VIEW_OVERRIDE: "viewOverrides",
  VIEW_SELECTOR: "viewSelectors",
  PLAN_SET_TEMPLATE_CONFIG: "planSetTemplateConfigs",
  AUTO_DIMENSION: undefined,
  ENTITY_TAG: "entityTags",
  EFFECT: "effects",
  DATUM: "datums",
  RULE_BASED_SELECTOR: "ruleBasedSelectors",
  RULE_BASED_SELECTOR_INSTANCE: "ruleBasedSelectorInstances",
  LEVEL_DATUM: undefined,
  ROOF_SIDE: undefined,
  OPTION: undefined,
  CATALOG_ITEM: undefined,
  ANNOTATION_TEMPLATE: undefined,
  ANNOTATION_TEMPLATE_COMPONENT: undefined,
  ELECTRICAL_RULE: undefined,
  SHAPE: undefined,
  EDGE: undefined,
  VERTEX: undefined,
  EDIT_MODE: undefined,
  GENERATED_ENTITY: undefined,
};

function docGetTableEntity(doc: any | null | undefined, handle: any) {
  if (!doc || !handle) {
    return null;
  }

  const docTableName = docEntityTypeToDocTableName[handle.type];

  if (!docTableName) {
    return null;
  }

  if (!doc[docTableName]) {
    return null;
  }

  return doc[docTableName][handle.id];
}

const catalogSettingsTypeToCatalogFieldDescriptors = {
  ["ANNOTATION_CATALOG_SETTINGS"]: [{ fieldName: "annotationTemplates", catalogItemType: "ANNOTATION_TEMPLATE" }],
  ["CASEWORK_CATALOG_SETTINGS"]: [{ fieldName: "caseworkTypes", catalogItemType: "CASEWORK_DESCRIPTOR" }],
  ["DOOR_CATALOG_SETTINGS"]: [{ fieldName: "doorTypes", catalogItemType: "DOOR_DESCRIPTOR" }],
  ["WALL_CATALOG_SETTINGS"]: [
    { fieldName: "wallTypes", catalogItemType: "WALL_TYPE" },
    { fieldName: "wallMaterials", catalogItemType: "STRUCTURAL_MATERIAL" },
  ],
  ["DATUM_CATALOG_SETTINGS"]: [{ fieldName: "datums", catalogItemType: "DATUM" }],
  ["FLOOR_CATALOG_SETTINGS"]: [
    { fieldName: "floorAssemblies", catalogItemType: "FLOOR_ASSEMBLY_COMPONENTS" },
    { fieldName: "floorComponents", catalogItemType: "FLOOR_COMPONENT" },
    { fieldName: "structuralFloorMaterial", catalogItemType: "STRUCTURAL_MATERIAL" },
  ],
  ["STAIR_CATALOG_SETTINGS"]: [{ fieldName: "stairSettingsTable", catalogItemType: "STAIR_SETTING" }],
  ["COLUMN_CATALOG_SETTINGS"]: [{ fieldName: "columnTable", catalogItemType: "COLUMN_DESCRIPTOR" }],
  ["WINDOW_CATALOG_SETTINGS"]: [{ fieldName: "windowTypes", catalogItemType: "WINDOW_DESCRIPTOR" }],
  ["DRAWING_CATALOG_SETTINGS"]: [{ fieldName: "drawings", catalogItemType: "DRAWING" }],
  ["PROFILE_CATALOG_SETTINGS"]: [{ fieldName: "profileTable", catalogItemType: "PROFILE_EXTRUSION_DESCRIPTOR" }],
  ["RAILING_CATALOG_SETTINGS"]: [{ fieldName: "railingSettingsTable", catalogItemType: "RAILING_SETTING" }],
  ["FOUNDATION_CATALOG_SETTINGS"]: [
    { fieldName: "foundationAssemblies", catalogItemType: "FOUNDATION_ASSEMBLY_COMPONENTS" },
    { fieldName: "foundationComponents", catalogItemType: "FOUNDATION_COMPONENT" },
  ],
  ["ELECTRICAL_ITEM_CATALOG_SETTINGS"]: [{ fieldName: "loadTypes", catalogItemType: "LOAD_DESCRIPTOR" }],
  ["INTERIOR_PROP_SETTINGS"]: [{ fieldName: "propTypes", catalogItemType: "PROP_ATTRIBUTES" }],
  ["EXTERIOR_PROP_SETTINGS"]: [{ fieldName: "propTypes", catalogItemType: "PROP_ATTRIBUTES" }],
};

function docGetItemsFromCatalog(doc: any, catalog: any) {
  const settings = docGetTableEntity(doc, catalog);

  if (!settings) {
    return {};
  }

  if (!isCatalogSettings(settings)) {
    return {};
  }

  const descriptors = catalogSettingsTypeToCatalogFieldDescriptors[settings.type];

  if (!descriptors) {
    return {};
  }

  if (descriptors.length === 1) {
    return settings[descriptors[0].fieldName] ?? {};
  }

  return descriptors.reduce((acc, descriptor) => Object.assign(acc, settings[descriptor.fieldName]), {});
}

export function docGetEntityHandleById(doc: any, id: string | undefined, entityTypes: string[] = []): any {
  const entityTypeMap = {};

  if (id === undefined) {
    return null;
  }

  // due to the untyped nature (any) of entity.entityType, this protection is required
  if (!Array.isArray(entityTypes)) {
    throw new Error("docGetEntityHandleById requires an array of entityTypes if any");
  } else {
    for (const entityType of entityTypes.filter(Boolean)) {
      entityTypeMap[entityType] = entityType;
    }
  }

  const eligibleTypes = Object.keys(entityTypeMap).length ? entityTypeMap : docEntityHandleTypes;

  for (const type in eligibleTypes) {
    const entityType = type;

    switch (entityType) {
      case "CATALOG_ITEM": {
        for (const catalogId in doc.settings) {
          const settings = doc.settings[catalogId];
          if (isCatalogSettings(settings)) {
            const settingsHandle = { type: "SETTINGS", id: catalogId };
            const items = docGetItemsFromCatalog(doc, settingsHandle);
            if (items && items[id]) {
              return { type: "CATALOG_ITEM", itemId: id };
            }
          }
        }
        break;
      }
      case "PROP_ATTRIBUTES": {
        const propAttributes = doc.propAttributes?.[id];
        if (propAttributes) {
          return { type: "PROP_ATTRIBUTES", propAttributesId: id };
        }
        break;
      }
      case "OPTION_SET": {
        const optionSetId = optionSetIdNew(id);

        if (doc.optionSets[optionSetId]) {
          return { type: "OPTION_SET", id: optionSetId };
        }
        break;
      }
      case "OPTION": {
        const optionId = optionIdNew(id);

        let optionSetHandle: any = null;

        for (const optionSetId in doc.optionSets) {
          const optionSet = doc.optionSets[optionSetId];
          if (optionSet.options?.[optionId]) {
            optionSetHandle = { type: "OPTION_SET", id: optionSet.id };
            break;
          }
        }

        if (optionSetHandle) {
          return { type: "OPTION", optionKey: optionId, optionSetId: optionSetHandle.id };
        }

        break;
      }
      case "ELECTRICAL_RULE": {
        for (const settingsId in doc.settings) {
          const settings = doc.settings[settingsId];

          if (settings.type !== "ELECTRICAL_SETTINGS") {
            continue;
          }

          if (settings.rules?.[id]) {
            return { type: "ELECTRICAL_RULE", id, settingsHandle: { type: "SETTINGS", id: settingsId } };
          }
        }

        break;
      }
      case "ANNOTATION_TEMPLATE": {
        for (const settingsId in doc.settings) {
          const settings = doc.settings[settingsId];

          if (settings.type !== "ANNOTATION_CATALOG_SETTINGS") {
            continue;
          }

          if (settings.annotationTemplates?.[id]) {
            return {
              type: "ANNOTATION_TEMPLATE",
              annotationTemplateId: id,
              settingsHandle: { type: "SETTINGS", id: settingsId },
            };
          }
        }

        break;
      }
      case "ANNOTATION_TEMPLATE_COMPONENT": {
        for (const settingsId in doc.settings) {
          const settings = doc.settings[settingsId];

          if (settings.type !== "ANNOTATION_CATALOG_SETTINGS") {
            continue;
          }

          for (const templateId in settings.annotationTemplates) {
            const annotationTemplate = settings.annotationTemplates[templateId];

            if (annotationTemplate.components?.[id]) {
              return {
                type: "ANNOTATION_TEMPLATE_COMPONENT",
                componentId: id,
                annotationTemplateHandle: {
                  type: "ANNOTATION_TEMPLATE",
                  annotationTemplateId: templateId,
                  settingsHandle: { type: "SETTINGS", id: settingsId },
                },
              };
            }
          }
        }

        break;
      }
      case "LEVEL_DATUM":
      case "SHAPE":
      case "EDGE":
      case "VERTEX":
      case "ROOF_SIDE":
      case "EDIT_MODE":
      case "GENERATED_ENTITY":
      case "AUTO_DIMENSION":
        continue;

      default: {
        const docField = docEntityTypeToDocTableName[entityType];

        if (!docField) {
          continue;
        }

        if (doc[docField]?.[id]) {
          return { type: entityType, id };
        }
      }
    }
  }

  return null;
}

enum SystemOptionSet {
  ELEVATION_STYLE = "elevationStyle",
  MIRROR = "mirror",
  INTERIOR_PALETTE = "interiorPalette",
}

function isSystemOptionSetId(optionSetId: string): boolean {
  return Object.values<string>(SystemOptionSet).includes(optionSetId);
}

function optionSetIdNew(id?: string | number): string {
  const optionSetId = String(id ? id : uid());

  if (isSystemOptionSetId(optionSetId)) {
    return optionSetId;
  }

  if (!optionSetId.startsWith(OPTION_SET_ID_PREFIX)) {
    return OPTION_SET_ID_PREFIX + optionSetId;
  }

  return optionSetId;
}

function optionIdNew(id?: string | number, suffix?: string): string {
  const optionId = String(id ? id : uid());

  if (optionId === "true" || optionId === "false") {
    return optionId;
  }

  if (!optionId.startsWith(OPTION_ID_PREFIX)) {
    return `${OPTION_ID_PREFIX}${optionId}${suffix ?? ""}`;
  }

  return `${optionId}${suffix ?? ""}`;
}
